<template>
  <div class="sidebar">
    <div class="logo">
      <img class="logo-img" src="../../public/img/wordmark.png" alt="wholebodyapp logo">
      <span>Whole Body Arkansas </span>
    </div>
    <div class="context-switcher app-ui-10">
      <ContextSwitch
        v-model="scope"
        :options="scopeOptions"
        optionLabel="name"
        optionValue="id"
        @change="onContextChange"
      />
    </div>
    <div class="search">
      <InputText v-model="search" placeholder="Quick search..." />
    </div>

    <div class="nav">
      <SidebarItem
        v-for="item, index in userSidebarItems"
        :key="item.text"
        v-model:item="userSidebarItems[index]"
      />
    </div>
  </div>
</template>


<script setup>
import { useRoute, useRouter } from 'vue-router';
import { inject, onMounted, ref, toRaw, watch } from 'vue';
import SidebarItem from './sidebar-item.vue';
import ContextSwitch from './sidebar-context.vue';
import InputText from '@wholebodyapp/ui-components/src/components/input-text/input-text.vue';
import { useContextStore } from '@wholebodyapp/stores';
import { minimatch } from 'minimatch';

const myGlobalVariable = inject('myGlobalVariable');
const appData = inject('appData');
const context = useContextStore();
const scope = ref();
const permissionMap = context.getPermissionMap;
const user = context.getUser;
const scopeOptions = Object
  .entries(context.permissionMap)
  .map(([key, value]) => ({id: key, name: value.name}));
const router = useRouter();
const userSidebarItems = ref([]);
const search = ref(null);

onMounted(() => {
  scope.value = context?.getScope || user?.defaultScope;
});

/**
 *  When the user changes scope using the context switcher, we update the
 *  context in the global context, update grants, and send them to the
 *  dashboard, since they may, or may not, have current grants for the
 *  present view.
 *  @return    {[type]}    [description]
 */
function onContextChange () {

  context.setScope(scope.value);
  // const grants = context.permissionMap?.[scope.value]?.grants;
  router.push('/dashboard');
}


// this watcher handles the sidebar items!
watch(
  [ scope, search ],
  ([newScope, newSearch]) => {
    const grants = context.permissionMap?.[newScope]?.grants;
    const scopeSidebarItems = filterAppItems(
      structuredClone(appData.sidebarItems.value),
      grants
    );
    const searchSidebarItems = searchItems(
      scopeSidebarItems,
      newSearch
    );
    userSidebarItems.value = searchSidebarItems;
  }
);

/**
 *  The app provides us *all* possible sidebar items that
 *  a generic user *could* have. This function filters out
 *  any sidebarItems that the user does not have permission
 *  to use. Exclusively a convenince, users can bypass this.
 *  Yep, it's recursive so be careful with it.
 *
 *  @return {[type]} [description]
 */
const filterAppItems = (items, grants) => {
  return items.filter(eaItem => {
    if (eaItem.children) {
      eaItem.children = filterAppItems(eaItem.children, grants);
      eaItem.expand = eaItem.expand || false;
      return eaItem.children.length; // no children, no care!
    }
    if (eaItem.path && eaItem.permissions) {
      return eaItem.permissions.some(eaPermission =>
        grants.some(eaGrant => minimatch(eaGrant, eaPermission))
      );
    }
    return eaItem.path; // won't show if path is undefined or null!
  });
};

/**
 *  Searches sidebar items
 */
const searchItems = (items, search) => {
  return items.filter(eaItem => {
    // the label matches, we return it, and don't expand it, nor
    // filter the children.
    if (search ? eaItem.text.match(new RegExp(search, 'i')): true)
      return true;
    if (eaItem.children) {
      eaItem.children = searchItems(eaItem.children, search);
      if (search) eaItem.expand = true;
      return eaItem.children.length;
    }
    return false;
  });
};



</script>


<style lang="scss" scoped>
@use '@wholebodyapp/design/colors/palette' as *;
@use '@wholebodyapp/design/typography/weights' as *;

.sidebar {
  border-right: 1px solid $gray-200;
  color: var(--text-500);
  overflow: auto;
}

.sidebar .logo {
  align-items: center;
  display: flex;
}

.sidebar .logo-img {
  height: 32px;
  margin: 16px 24px;
}


.sidebar .context-switcher {
  color: var(--primary-text);
  display: flex;
  align-items: center;
  padding: 0 24px;
  margin-top: 24px;
}

.sidebar .nav {
  margin-left: 16px;
  margin-right: 24px;
}

.search {
  padding: 24px 24px;
}

</style>
